import { useQuery } from "@apollo/client/react";
import type { PlainDateString } from "@sa-nu/shared-utils/date";
import {
  PlainDate,
  formatPlainDate,
  getCurrentDate,
  parsePlainDate,
} from "@sa-nu/shared-utils/date";

import { graphql } from "../../../__generated__/gql";
import type {
  AccommodationReservationProvider,
  StayTicketType,
} from "../../../__generated__/gql/graphql";
import { buildAccommodationReservationProviderInput } from "../../../utils";

// 予約フォームの draft query に colocation せず単独で引く。移行期間が終わればこのファイルごと消すのと、
// 引数が provider だけなので宿泊日を変えても Apollo のキャッシュが効き再リクエストが不要になるため
const Query = graphql(`
  query ReservationRuleRevision2027Policy(
    $provider: AccommodationReservationProviderInput!
  ) {
    reservationRuleRevision2027Policy(provider: $provider) {
      announcementStartDate
      firstApplicableNight
    }
  }
`);

/**
 * 2027年度改定に伴う予約フォームの追加料金条件の表示切り替え。
 * 改定の適用開始宿泊日を provider ごとに API から取得し、滞在との関係から表示を決める。
 * 移行期間（forBiz の移行が終わる 2028/5 宿泊）が終わったら hook ごと削除する
 */
export function useRuleRevision2027ForStay({
  provider,
  reservation,
}: {
  provider: AccommodationReservationProvider;
  reservation: {
    firstNight: PlainDateString;
    lastNight: PlainDateString;
    stayTicketUnit?: { reservationInfo: { type: StayTicketType } } | null;
  };
}): {
  appliesFrom: string | null;
  isStraddling: boolean;
  isArchitectureTypeAdditionalFeeDeprecated: boolean;
} {
  // Stay Gift from Owners は BE が発行元オーナーの免除ルールで計算するので、オーナー扱いにする
  const isOwner =
    provider.__typename === "CoOwnersPlanAccommodationReservationProvider" ||
    provider.__typename === "FullOwnerPlanAccommodationReservationProvider" ||
    reservation.stayTicketUnit?.reservationInfo.type === "StayGiftFromOwners";
  const isBusinessPlan =
    provider.__typename === "BusinessPlanAccommodationReservationProvider";

  const { data } = useQuery(Query, {
    variables: {
      provider: buildAccommodationReservationProviderInput(provider),
    },
    skip: !isOwner && !isBusinessPlan,
  });
  const firstApplicableNight =
    data?.reservationRuleRevision2027Policy.firstApplicableNight;
  if (firstApplicableNight === undefined) {
    return {
      appliesFrom: null,
      isStraddling: false,
      isArchitectureTypeAdditionalFeeDeprecated: false,
    };
  }

  const revisionNight = parsePlainDate(firstApplicableNight);
  const position = resolveStayPositionToRuleRevision2027({
    firstNight: parsePlainDate(reservation.firstNight),
    lastNight: parsePlainDate(reservation.lastNight),
    firstApplicableNight: revisionNight,
  });
  return {
    appliesFrom: formatPlainDate(revisionNight),
    isStraddling: position === "Straddling",
    isArchitectureTypeAdditionalFeeDeprecated: position === "After",
  };
}

export type RuleRevision2027AsOfToday =
  | { status: "BeforeAnnouncement"; firstApplicableNight: PlainDate | null }
  | {
      status: "AnnouncementStarted" | "FirstApplicableNightArrived";
      firstApplicableNight: PlainDate;
    };

// 宿泊日でなく今日を基準に、改定の案内や移行の進み具合で表示を切り替える画面向け
export function useRuleRevision2027AsOfToday({
  provider,
}: {
  provider: AccommodationReservationProvider;
}): RuleRevision2027AsOfToday {
  const { data } = useQuery(Query, {
    variables: {
      provider: buildAccommodationReservationProviderInput(provider),
    },
  });
  const policy = data?.reservationRuleRevision2027Policy;
  if (policy === undefined) {
    return { status: "BeforeAnnouncement", firstApplicableNight: null };
  }

  const today = getCurrentDate();
  const firstApplicableNight = parsePlainDate(policy.firstApplicableNight);
  const status =
    PlainDate.compare(firstApplicableNight, today) <= 0
      ? "FirstApplicableNightArrived"
      : PlainDate.compare(
            parsePlainDate(policy.announcementStartDate),
            today,
          ) <= 0
        ? "AnnouncementStarted"
        : "BeforeAnnouncement";
  return { status, firstApplicableNight };
}

function resolveStayPositionToRuleRevision2027({
  firstNight,
  lastNight,
  firstApplicableNight,
}: {
  firstNight: PlainDate;
  lastNight: PlainDate;
  firstApplicableNight: PlainDate;
}): "Before" | "Straddling" | "After" {
  if (PlainDate.compare(lastNight, firstApplicableNight) < 0) {
    return "Before";
  }
  if (PlainDate.compare(firstNight, firstApplicableNight) >= 0) {
    return "After";
  }
  return "Straddling";
}
